import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Sparkles, Eye, EyeOff } from "lucide-react";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import SEO from "@/components/SEO";
import ResendVerificationInline from "@/components/ResendVerificationInline";
import SocialLoginButtons from "@/components/SocialLoginButtons";
import { track, EVENT } from "@/lib/analytics";

const SAFE_NEXT = /^\/(?!\/)[^\s]{0,510}$/;

export default function Login() {
  const { login } = useCollectorAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const rawNext = new URLSearchParams(location.search).get("next");
  const isSafeNext = typeof rawNext === "string"
    && rawNext.startsWith("/")
    && !rawNext.startsWith("//")
    && !/^\/*[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(rawNext);
  const nextParam = isSafeNext && SAFE_NEXT.test(rawNext) ? rawNext : null;
  // Iter V2 — preserve `?claim={cert_number}` through sign-in so the
  // authenticated collector lands on the claim form.
  const claimParam = new URLSearchParams(location.search).get("claim");
  const safeClaim = (typeof claimParam === "string" && /^[A-Z]{2}-\d{4}-\d{4}$/.test(claimParam)) ? claimParam : null;
  const claimDest = safeClaim ? `/dashboard/claim?cert=${encodeURIComponent(safeClaim)}` : null;
  const from = claimDest || nextParam || location.state?.from || "/dashboard";

  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const linkPending = new URLSearchParams(location.search).get("link_pending") === "1";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password, form.remember);
      track(EVENT.LOGIN_COMPLETED, { login_method: "password" },
            { once: true, key: "login" });
      toast.success("Signed in.");
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Sign-in failed.";
      toast.error(msg);
      if (typeof detail === "string" && /verify.*email/i.test(detail)) {
        setNeedsVerification(true);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-140px)] grid lg:grid-cols-2" data-testid="login-page">
      <SEO title="Sign in · Collector Registry" />

      {/* -------- LEFT: form column -------- */}
      <div className="flex items-center justify-center px-5 py-14 lg:py-20">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block kicker kicker-red mb-6" data-testid="login-brand">
            Gil Maakaroun · Collector Registry
          </Link>
          <h1 className="h-display text-4xl md:text-5xl leading-none">Welcome back.</h1>
          <p className="mt-3 text-[var(--smoke)] text-sm md:text-base">
            Sign in to manage your certificates, ownership claims, and profile.
          </p>

          {linkPending && (
            <div
              className="mt-6 rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-4 text-sm"
              data-testid="login-link-pending"
            >
              <div className="font-bold text-[var(--ink)]">Check your email</div>
              <div className="text-[var(--ink)] mt-1">
                We sent a confirmation link to finish linking your social provider to
                your existing collector account.
              </div>
            </div>
          )}

          <div className="mt-8 space-y-6">
            <SocialLoginButtons mode="login" returnTo={from} />

            {/* Phase C1 — prominent create-account button directly below
                the Google button so new visitors on mobile see it in
                the same eye-line. Preserves any pending claim / next
                context from the URL. */}
            <Link
              to={`/register${safeClaim ? `?claim=${encodeURIComponent(safeClaim)}` : (from !== "/dashboard" ? `?next=${encodeURIComponent(from)}` : "")}`}
              data-testid="login-create-account-primary"
              className="btn-black w-full justify-center text-sm"
            >
              Create Collector account
            </Link>

            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[var(--smoke)]" aria-hidden>
              <div className="flex-1 h-px bg-[var(--line)]" />
              or sign in with email
              <div className="flex-1 h-px bg-[var(--line)]" />
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
              <label className="block">
                <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">
                  Email
                </div>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="login-email"
                  className="gm-field"
                  placeholder="you@example.com"
                />
              </label>

              <label className="block">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase">
                    Password
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-bold text-[var(--panini-red)] hover:underline"
                    data-testid="login-forgot"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="login-password"
                    className="gm-field pr-11"
                    placeholder="Your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] hover:text-[var(--ink)]"
                    aria-label={showPass ? "Hide password" : "Show password"}
                    data-testid="login-password-toggle"
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              <label className="flex items-center gap-3 text-sm text-[var(--ink)]">
                <input
                  type="checkbox"
                  checked={form.remember}
                  onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                  data-testid="login-remember"
                  className="w-4 h-4"
                />
                <span>Keep me signed in for 30 days</span>
              </label>

              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit"
                className="btn-red w-full justify-center text-sm disabled:opacity-60"
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div
              data-testid="login-resend-block"
              className={needsVerification ? "" : "hidden"}
            >
              <ResendVerificationInline
                email={needsVerification ? form.email : ""}
                testidPrefix="login"
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[var(--line)] text-sm text-[var(--smoke)]">
            New here?{" "}
            <Link
              to={`/register${safeClaim ? `?claim=${encodeURIComponent(safeClaim)}` : (from !== "/dashboard" ? `?next=${encodeURIComponent(from)}` : "")}`}
              className="font-bold text-[var(--ink)] hover:text-[var(--panini-red)]"
              data-testid="login-register"
            >
              Create your collector account →
            </Link>
          </div>
        </div>
      </div>

      {/* -------- RIGHT: brand column -------- */}
      <BrandPanel />
    </div>
  );
}

function BrandPanel() {
  return (
    <div className="hidden lg:flex relative overflow-hidden bg-[var(--ink)] text-white items-center justify-center px-14">
      <div className="absolute inset-0 opacity-95">
        <div className="absolute inset-0"
             style={{
               background:
                 "radial-gradient(700px 400px at 20% 10%, rgba(255,203,5,0.22), transparent 60%)," +
                 "radial-gradient(600px 400px at 80% 90%, rgba(227,6,19,0.28), transparent 60%)," +
                 "radial-gradient(500px 400px at 60% 40%, rgba(10,123,62,0.18), transparent 60%)",
             }} />
        <div className="absolute inset-0"
             style={{
               backgroundImage:
                 "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
                 "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
               backgroundSize: "40px 40px",
             }} />
      </div>
      <div className="relative max-w-sm">
        <div className="text-[11px] tracking-[0.24em] font-bold text-[var(--panini-yellow)] uppercase">
          Collector Registry
        </div>
        <h2 className="mt-4 font-display text-4xl leading-tight">
          Your permanent<br/> collector identity.
        </h2>
        <p className="mt-4 text-white/75 text-base leading-relaxed">
          One account for every certificate, every album, every trade.
          Backed by cryptographic claim codes and moderated legacy proofs.
        </p>
        <ul className="mt-8 space-y-3">
          <Feature icon={<CheckCircle2 size={18} />} label="Instant claim via one-time codes" />
          <Feature icon={<ShieldCheck size={18} />} label="Private, encrypted proof review" />
          <Feature icon={<Sparkles size={18} />} label="Public Collector ID · GM-C000001" />
        </ul>
      </div>
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <li className="flex items-center gap-3 text-sm text-white/90">
      <span className="w-8 h-8 rounded-full bg-white/10 border border-white/15 grid place-items-center text-[var(--panini-yellow)]">
        {icon}
      </span>
      {label}
    </li>
  );
}
